import {
  aggregateWeaponStats,
  eyeHeightFor,
  fittedModsDeep,
  hasSuppressor,
  loadedAmmo,
  opticZoom,
  recoilMultiplier,
  sightZeroElevationRad,
  clampPitch,
  ALL_ITEM_DEFS,
  weaponWithAmmo,
  type AmmoItemDef,
  type EffectiveWeapon,
  type ItemInstance,
  type Vec3,
  type WeaponItemDef,
} from "@rietz/shared";
import type { Object3D } from "three";
import type { GameAudio } from "../../audio/synth.js";
import type { Input } from "../../input/input.js";
import type { Prediction } from "../../net/prediction.js";
import type { Viewmodel } from "../../render/viewmodel.js";
import { pulseHitMarker, setHideoutRange } from "../../ui/store.js";
import type { AimSystem } from "../aim.js";
import type { VisualBullets } from "../bullets.js";
import type { Fx } from "../fx.js";
import { applyLeanOffset } from "../lean.js";
import type { RangeTarget } from "./layout.js";
import {
  deviate,
  drillSecLeft,
  hitTarget,
  scoreHit,
  startDrill,
  stepDrill,
  type DrillState,
} from "./rangeLogic.js";

/**
 * THE SHOOTING RANGE: `FireControl` with everything the server owns removed.
 * No socket, magazine, reload, wear, death or damage — what remains is the rpm
 * gate, the eye the shot leaves from, hip spread closing as the sights come
 * up, the buckshot cone, the two kicks, flash and report, and a round flown by
 * the shared ballistics against the same collision world into plates that
 * count. A separate class rather than an `if (range)` through the raid's fire
 * path, whose whole subject is reconciling against a remote authority.
 *
 * The weapon is the kit's own (`pickRangeWeapon`), folded through the same
 * accessors `InventoryController.refreshEquipped` uses, so a suppressor or
 * scope fitted at the bench behaves down here. The cartridge is the loaded
 * one, or the first round of the caliber when the magazine is empty or absent.
 *
 * Range level 2: F at the firing point starts a thirty-second drill in which
 * one plate scores at a time; pressing F again restarts it.
 */

export interface RangeControlOptions {
  input: Input;
  prediction: Prediction;
  viewmodel: Viewmodel;
  /** Recoil and sway. The same object the loop poses the camera with, for the
   *  same reason as in a raid: the round goes where the sights are. */
  aim: AimSystem;
  fx: Fx;
  audio: GameAudio;
  bullets: VisualBullets;
}

/** What the session hands over when the player steps up to the firing point. */
export interface RangeWeapon {
  def: WeaponItemDef;
  /** `null` only for a def with no instance behind it, which nothing currently
   *  produces — the fallback carbine is minted with its factory parts. */
  item: ItemInstance | null;
  /** Display name for the scoreboard. */
  name: string;
}

/**
 * Buckshot half-angle, radians — the same 2.5° `fireControl.ts` mirrors; here
 * it is the whole truth, since nobody else rolls pellets.
 */
const PELLET_CONE_RAD = (2.5 * Math.PI) / 180;

/**
 * Slack on a plate's box when matching a round, metres: the ballistics stop a
 * round ON the surface, so exact containment would be a coin flip.
 */
const PLATE_TOLERANCE_M = 0.03;

/**
 * Best drill score this browser session — a "beat your last run" needle, not a
 * statistic, so module-level and never persisted.
 */
let bestDrillScore = 0;

export class RangeControl {
  private readonly o: RangeControlOptions;

  private on = false;

  // --- The gun, derived once per `start` -----------------------------------
  private eff: EffectiveWeapon | null = null;
  private fireIntervalMs = Infinity;
  private hipSpreadRad = 0;
  private zeroElevationRad = 0;
  private pellets = 1;
  private autoFire = false;
  private suppressed = false;
  private weaponSoundId = "";
  private weaponName = "";

  // --- The bay -------------------------------------------------------------
  private targets: readonly RangeTarget[] = [];
  private targetIds: readonly string[] = [];
  private glows: ReadonlyMap<string, Object3D> = new Map();
  private drillAvailable = false;

  // --- The session's tally -------------------------------------------------
  private shots = 0;
  private hits = 0;
  private drill: DrillState | null = null;
  /** Which plate's glow is currently switched on, so the visibility flags are
   *  written on a CHANGE rather than on every frame. `""` = none. */
  private litGlowId = "";

  /**
   * The scoreboard as last published, fieldwise so the steady-state frame costs
   * six comparisons and no allocation.
   */
  private pubShots = -1;
  private pubHits = -1;
  private pubSecLeft: number | null = null;
  private pubScore = -1;
  private pubBest = -1;
  private pubName = "";

  private lastShotAtMs = -Infinity;

  /** Scratch — the fire path runs inside the render loop and must not allocate. */
  private readonly eye: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly dir: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly pellet: Vec3 = { x: 0, y: 0, z: 0 };

  constructor(opts: RangeControlOptions) {
    this.o = opts;
  }

  get active(): boolean {
    return this.on;
  }

  /**
   * Steps up to the firing point. Everything expensive about the weapon is
   * derived here once, as in `FireControl.syncWeapon`; nothing about the gun
   * can change while the player stands here.
   */
  start(
    weapon: RangeWeapon,
    targets: readonly RangeTarget[],
    glows: ReadonlyMap<string, Object3D>,
    drillAvailable: boolean,
  ): void {
    const { def, item } = weapon;
    const eff = weaponWithAmmo(aggregateWeaponStats(def, item), rangeAmmoFor(def, item));

    this.on = true;
    this.eff = eff;
    this.fireIntervalMs = 60000 / eff.rpm;
    this.hipSpreadRad = (eff.hipSpreadDeg * Math.PI) / 180;
    this.zeroElevationRad = sightZeroElevationRad(eff);
    this.pellets = eff.pellets;
    this.autoFire = def.fireMode === "auto";
    this.suppressed = hasSuppressor(def, item);
    this.weaponSoundId = def.id;
    this.weaponName = weapon.name;

    this.targets = targets;
    this.targetIds = targets.map((t) => t.id);
    this.glows = glows;
    this.drillAvailable = drillAvailable;

    this.shots = 0;
    this.hits = 0;
    this.drill = null;
    this.lastShotAtMs = -Infinity;
    this.setLitGlow("");
    this.forgetPublished();

    // Word for word what `FireControl.syncWeapon` hands the two of them: one
    // folded kick scale for the drawn gun and the thrown view.
    const recoilMult = def.recoil * recoilMultiplier(def, item);
    this.o.viewmodel.setWeapon(def.id, {
      adsTimeSec: eff.adsTimeSec,
      mods: fittedModsDeep(def, item).map((f) => f.def.id),
      recoilMult,
      zoom: opticZoom(def, item),
      reloadSec: eff.reloadSec,
      reloadTopup: def.reloadStyle === "topup",
    });
    this.o.aim.setWeapon({ recoil: recoilMult, ergonomics: eff.ergonomics });
  }

  /**
   * Steps away — walked out of the bay, Escape, or a rebuild. Empties the
   * hands: outside the bay the player is a civilian in their own cellar.
   * Idempotent.
   */
  stop(): void {
    if (!this.on) return;
    this.on = false;
    this.eff = null;
    this.drill = null;
    this.setLitGlow("");
    this.targets = [];
    this.targetIds = [];
    this.glows = new Map();
    this.o.viewmodel.setWeapon(null, { adsTimeSec: 0 });
    this.o.aim.setWeapon(null);
    setHideoutRange(null);
    this.forgetPublished();
  }

  /**
   * F at the firing point: start a drill, or restart the running one. A no-op
   * below range level 2.
   */
  onInteract(nowMs: number): void {
    if (!this.on || !this.drillAvailable) return;
    if (this.targetIds.length === 0) return;
    this.drill = startDrill(this.targetIds, nowMs, Math.random);
    this.setLitGlow(this.drill.litId);
  }

  /**
   * One frame at the firing point. Presses are drained UNCONDITIONALLY, as in
   * `FireControl.update` — a click made while the panel was up is stale intent.
   */
  update(nowMs: number): void {
    const presses = this.o.input.consumeFirePresses();
    if (!this.on || this.eff === null) return;

    if (this.autoFire) {
      // Held trigger: the rpm gate below IS the cadence. The buffered press
      // still counts, for a click that began and ended inside one frame.
      if (this.o.input.fireHeld || presses > 0) {
        if (this.canFire(nowMs)) this.fire(nowMs);
      }
    } else {
      let remaining = presses;
      while (remaining > 0) {
        remaining -= 1;
        if (!this.canFire(nowMs)) break;
        this.fire(nowMs);
      }
    }

    // The drill AFTER the trigger, so a frame holding a drill's last shot and
    // its final tick scores the shot.
    const drill = this.drill;
    if (drill !== null) {
      const next = stepDrill(drill, this.targetIds, nowMs, Math.random);
      if (next === null) {
        this.drill = null;
        this.setLitGlow("");
        if (drill.score > bestDrillScore) bestDrillScore = drill.score;
      } else if (next !== drill) {
        this.drill = next;
        this.setLitGlow(next.litId);
      }
    }

    this.publish(nowMs);
  }

  /**
   * A round of ours stopped somewhere (`VisualBullets`' impact hook). No remote
   * rounds exist here, but the flag is honoured rather than assumed.
   */
  onImpact(pos: Readonly<Vec3>, local: boolean): void {
    if (!this.on || !local) return;
    const t = hitTarget(this.targets, pos, PLATE_TOLERANCE_M);
    if (t === null) return;

    // The hook carries no clock; one reading here stamps both marker and drill.
    const nowMs = performance.now();
    this.hits += 1;
    this.o.audio.hitConfirm();
    // The "hit" marker, not the predicted one: a plate is a box only this
    // client knows about, so its own answer is authoritative.
    pulseHitMarker("hit", nowMs);

    const drill = this.drill;
    if (drill === null) return;
    const after = scoreHit(drill, this.targetIds, t.id, nowMs, Math.random);
    if (after === drill) return;
    this.drill = after;
    this.setLitGlow(after.litId);
  }

  // -----------------------------------------------------------------------

  private canFire(nowMs: number): boolean {
    return nowMs - this.lastShotAtMs >= this.fireIntervalMs;
  }

  private fire(nowMs: number): void {
    this.lastShotAtMs = nowMs;
    this.shots += 1;

    // The PREDICTED eye, leaned — leaning out from behind the divider must put
    // the muzzle past it.
    const state = this.o.prediction.state;
    this.eye.x = state.pos.x;
    this.eye.y = state.pos.y + eyeHeightFor(state.stance);
    this.eye.z = state.pos.z;
    applyLeanOffset(this.eye, this.o.input.yaw, this.o.input.lean);

    this.aimDirection(this.dir);

    // Two kicks after the direction was taken: this shot flies where the
    // sights were, the next pays — see `FireControl.fire`.
    this.o.viewmodel.kick();
    this.o.aim.kick();
    this.o.fx.muzzleFlash();
    this.o.audio.localShot(this.weaponSoundId, this.suppressed);

    const eff = this.eff;
    if (eff === null) return;
    if (this.pellets <= 1) {
      this.o.bullets.spawn(this.eye, this.dir, "", true, eff);
      return;
    }
    for (let i = 0; i < this.pellets; i++) {
      this.pellet.x = this.dir.x;
      this.pellet.y = this.dir.y;
      this.pellet.z = this.dir.z;
      // sqrt for a uniform disc — a linear roll bunches the pattern in the
      // middle, and buckshot emphatically does not do that.
      deviate(this.pellet, PELLET_CONE_RAD * Math.sqrt(Math.random()), Math.random);
      this.o.bullets.spawn(this.eye, this.pellet, "", true, eff);
    }
  }

  /**
   * Unit fire direction: view angles, sight zero, hip spread fading as the
   * sights come up. `FireControl.aimDirection` minus the wear cone — nothing
   * here costs the gun a point, so no penalty applies.
   */
  private aimDirection(out: Vec3): void {
    const aim = this.o.aim;
    const yaw = this.o.input.yaw + aim.offsetYaw;
    // The zero rides on the pitch OUTSIDE the clamp, as in the raid.
    const pitch = clampPitch(this.o.input.pitch + aim.offsetPitch) + this.zeroElevationRad;
    const cp = Math.cos(pitch);

    out.x = -Math.sin(yaw) * cp;
    out.y = Math.sin(pitch);
    out.z = -Math.cos(yaw) * cp;

    const spreadScale = 1 - this.o.viewmodel.adsT;
    if (spreadScale <= 0.001) return;
    deviate(out, this.hipSpreadRad * Math.sqrt(Math.random()) * spreadScale, Math.random);
  }

  /** Switches the glow on one plate and off the last one. Writes `.visible`
   *  only on a change, so a steady drill costs nothing. */
  private setLitGlow(id: string): void {
    if (id === this.litGlowId) return;
    const prev = this.glows.get(this.litGlowId);
    if (prev !== undefined) prev.visible = false;
    const next = this.glows.get(id);
    if (next !== undefined) next.visible = true;
    this.litGlowId = id;
  }

  /** Pushes the scoreboard at the HUD, but only when one of its six numbers
   *  has actually moved — see `pubShots` and friends. */
  private publish(nowMs: number): void {
    const drill = this.drill;
    const secLeft = drill === null ? null : drillSecLeft(drill, nowMs);
    const score = drill?.score ?? 0;
    if (
      this.shots === this.pubShots &&
      this.hits === this.pubHits &&
      secLeft === this.pubSecLeft &&
      score === this.pubScore &&
      bestDrillScore === this.pubBest &&
      this.weaponName === this.pubName
    ) {
      return;
    }
    this.pubShots = this.shots;
    this.pubHits = this.hits;
    this.pubSecLeft = secLeft;
    this.pubScore = score;
    this.pubBest = bestDrillScore;
    this.pubName = this.weaponName;
    setHideoutRange({
      weaponName: this.weaponName,
      shots: this.shots,
      hits: this.hits,
      drillSecLeft: secLeft,
      drillScore: score,
      best: bestDrillScore,
    });
  }

  /** Forces the next `publish` through, whatever it holds. */
  private forgetPublished(): void {
    this.pubShots = -1;
    this.pubHits = -1;
    this.pubSecLeft = null;
    this.pubScore = -1;
    this.pubBest = -1;
    this.pubName = "";
  }
}

// ---------------------------------------------------------------------------

/**
 * The cartridge the range flies: what is loaded, else the first round of the
 * gun's caliber in the item db (a linear scan, once per visit). `null` — a
 * caliber with no ammo, impossible today — makes `weaponWithAmmo` use the
 * weapon's own numbers.
 */
function rangeAmmoFor(def: WeaponItemDef, item: ItemInstance | null): AmmoItemDef | null {
  const loaded = loadedAmmo(def, item);
  if (loaded !== null) return loaded;
  for (const d of ALL_ITEM_DEFS) {
    if (d.kind === "ammo" && d.caliber === def.caliber) return d;
  }
  return null;
}
